import { screen, userAgent } from 'bits/browser'
import { currentUrl } from 'bits/location'
import throttle from 'lodash.throttle'
import { User } from 'types'

interface SimplifiedElement {
  tag: string
  class: string
  id: string
}

interface Config {
  user: any
  environment: string
}

interface EventProperties {
  [key: string]: any
}

interface EventContext {
  screen: any
  userAgent: string
  url: string
}

interface EventUser {
  id: string
  tenantId: string
  paying: boolean
  role: string
  lang: string
  createdAt: Date
  wallsUsed: number
  isRegistered: boolean
}

interface EventData {
  name: string
  props: EventProperties
  user: EventUser
  context: EventContext
  timestamp: number
}

let user: User
let environment = 'development'
let eventQueue: EventData[] = []

// Amazon API gateway that capture any data you send them and forward data to an S3 bucket.
// https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/ma73hwoovf/resources/uc2n3xooo3
const API_ENDPOINTS = {
  development: 'https://ma73hwoovf.execute-api.us-east-1.amazonaws.com/dev/streams/events_dev/record',
  staging: 'https://ma73hwoovf.execute-api.us-east-1.amazonaws.com/staging/streams/events_staging/record',
  production: 'https://ma73hwoovf.execute-api.us-east-1.amazonaws.com/prod/streams/events_prod/record',
}

function getUser(): EventUser {
  const eventUser: EventUser = {
    id: user.id.toString(),
    tenantId: user.tenant_id.toString(),
    paying: !!user.paying,
    role: user.role || 'member',
    lang: user.lang,
    createdAt: user.created_at,
    wallsUsed: user.quota.walls_used,
    isRegistered: !!user.registered_at,
  }
  return eventUser
}

function getBeaconEndpoint(): string {
  return API_ENDPOINTS[environment]
}

function getContext(): EventContext {
  return { userAgent, screen, url: currentUrl() }
}

function makeRequest(data: EventData): void {
  if (navigator.sendBeacon) {
    navigator.sendBeacon(getBeaconEndpoint(), JSON.stringify(data))
  }
}

function getRelativeTimestamp(): number {
  if (typeof window !== 'undefined' && window.performance) {
    return Math.round(window.performance.now() / 1000)
  }
  return -1
}

function getCurrentTimestamp(): number {
  return new Date().getTime()
}

function getSelectorStringFromHTMLElement(domElement: HTMLElement): string {
  let selectorString: string = domElement.tagName.toLowerCase()
  // For some reason, we are getting className.split is not a function
  if (domElement.className && domElement.className.split) {
    selectorString += `.${domElement.className.split(' ').join('.')}`
  }
  if (domElement.id) {
    selectorString += `#${domElement.id}`
  }
  return selectorString
}

function enqueueEvent(data: EventData): void {
  eventQueue.push(data)
}

function dequeueEvents(): void {
  if (navigator.sendBeacon && eventQueue.length > 0) {
    const stringifiedEventQueue: string[] = eventQueue.map((x: EventData): string => JSON.stringify(x))
    const payload: string = stringifiedEventQueue.join('\n')
    navigator.sendBeacon(getBeaconEndpoint(), payload)
  }
  eventQueue = []
}

const throttledDequeueEvents = throttle(dequeueEvents, 1000, { trailing: true, leading: false })

function trackEvent(name: string, props: EventProperties = {}): void {
  enqueueEvent({ name, props, user: getUser(), context: getContext(), timestamp: getCurrentTimestamp() })
  throttledDequeueEvents()
}

function captureEveryClick(): void {
  document.addEventListener('click', (e): void => {
    const eventTarget = e.target as HTMLElement
    if (eventTarget) {
      const target = getSelectorStringFromHTMLElement(eventTarget)
      const coordinates = { x: e.pageX, y: e.pageY }
      const elapsedTime = getRelativeTimestamp()
      trackEvent('click', { target, coordinates, elapsedTime })
    }
  })
}

const WHITELISTED_CLICK_SELECTOR = 'button,a,[role=button],[data-pepin-trigger=click]'
function extractExtraDataProps(dataset: DOMStringMap): Record<string, any> {
  const extraData = {}
  Object.keys(dataset).forEach((dataKey: string): void => {
    if (dataKey.startsWith('pepin') && dataKey !== 'pepinId' && dataKey !== 'pepinTrigger') {
      const strippedDataKey = dataKey.slice(5)
      const camelizedDataKey = strippedDataKey.substr(0, 1).toLowerCase() + strippedDataKey.substr(1)
      extraData[camelizedDataKey] = dataset[dataKey]
    }
  })
  return extraData
}

function recordWhitelistedClick(e: MouseEvent): void {
  const eventTarget = e.target as HTMLElement
  const closestElementInSelectorList = eventTarget.closest(WHITELISTED_CLICK_SELECTOR) as HTMLElement
  if (closestElementInSelectorList) {
    const eventName = 'click'
    const target = closestElementInSelectorList.dataset.pepinId || closestElementInSelectorList.id
    const extraData = extractExtraDataProps(closestElementInSelectorList.dataset)
    if (target) {
      const coordinates = { x: e.pageX, y: e.pageY }
      const elapsedTime = getRelativeTimestamp()
      trackEvent(eventName, {
        target,
        coordinates,
        elapsedTime,
        ...extraData,
      })
    }
  }
}
function captureOnlyWhitelistedClicks(): void {
  document.addEventListener('click', recordWhitelistedClick)
}

function init(config: Config): void {
  user = config.user
  environment = config.environment
  window.addEventListener('beforeunload', dequeueEvents)
}

export { trackEvent, recordWhitelistedClick, captureEveryClick, captureOnlyWhitelistedClicks, init }
